import * as React from 'react';

export interface IUseIntersectionObserverRefInput {
	/**
	 * Called whenever the observed element intersects the viewport. Captured by ref so the caller
	 * does not need to memoize it.
	 */
	onIntersect: () => void;
	/**
	 * When false, no observer is attached (and any existing one is torn down). Toggling from
	 * `false` to `true` reattaches the observer to the currently-mounted node, which re-runs the
	 * intersection check immediately — exactly the behavior infinite-scroll callers want when
	 * `hasMore` flips back to `true` (e.g. after a search resets pagination).
	 */
	enabled?: boolean;
	/**
	 * Forwarded to `IntersectionObserver`'s `rootMargin`. Common: `'100px'` to fire slightly
	 * before the element becomes visible so the next page is loaded before the user notices.
	 */
	rootMargin?: string;
	/** Forwarded to `IntersectionObserver`'s `threshold`. */
	threshold?: number | number[];
}

/**
 * Returns a callback ref that wires the attached element into an `IntersectionObserver` and
 * fires `onIntersect` whenever it intersects. Single source of truth for infinite-scroll
 * observer behavior across `useInfinitePagination` and `<ListPage>`'s load-more sentinel.
 *
 * Lifecycle: the observer is rebuilt whenever `enabled`, `rootMargin`, or `threshold` change
 * (so the latest options take effect immediately) and torn down on detach / unmount.
 */
export function useIntersectionObserverRef({
	onIntersect,
	enabled = true,
	rootMargin,
	threshold,
}: IUseIntersectionObserverRefInput): (node: Element | null) => void {
	const onIntersectRef = React.useRef(onIntersect);
	React.useEffect(() => {
		onIntersectRef.current = onIntersect;
	});

	const observerRef = React.useRef<IntersectionObserver | null>(null);

	React.useEffect(() => () => observerRef.current?.disconnect(), []);

	return React.useCallback(
		(node: Element | null) => {
			observerRef.current?.disconnect();
			observerRef.current = null;
			if (!node || !enabled) return;
			observerRef.current = new IntersectionObserver(
				entries => {
					if (entries[0]?.isIntersecting) onIntersectRef.current();
				},
				{ rootMargin, threshold }
			);
			observerRef.current.observe(node);
		},
		[enabled, rootMargin, threshold]
	);
}
